import {
  cacheKeys,
  FIND_ROUTINE_ASSIGNMENTS,
  useCurrentWorkspace,
  useCustomMutation,
  useCustomQuery,
  WorkspaceType,
} from '@gripp/shared-logic';
import {
  Colors,
  SaveFormTop,
  SearchInput,
  TopModalBar,
  useGetDimensions,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import '../../shared/style/reactTabStyle.css';
import { EditRoutineAssetsList } from './editRoutineAssetsList';
import { RoutineAddonsAssignList } from './routineAddonsAssignList';

type Props = {
  routineId: string;
  title: string;
  showCheckboxColumn: boolean;
  onComplete: () => void;
  /** 'assets' | 'addons' – which tab to show when modal opens (default: assets) */
  initialTab?: 'assets' | 'addons';
};

const getTabIndex = (tab?: 'assets' | 'addons') => (tab === 'addons' ? 1 : 0);

export const RoutineAssetsForm = ({
  routineId,
  title,
  showCheckboxColumn,
  onComplete,
  initialTab,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { dimensions, onLayout } = useGetDimensions();
  const height = dimensions?.height ?? 0;
  const tableResultsBlockHeight = 50;
  const listMaxHeight = height - tableResultsBlockHeight;

  const [originalAssetAssignments, setOriginalAssetAssignments] = useState<
    string[] | undefined
  >();
  const [assetAssignments, setAssetAssignments] = useState<
    string[] | undefined
  >(undefined);
  const [originalAddonAssignments, setOriginalAddonAssignments] = useState<
    string[] | undefined
  >();
  const [addonAssignments, setAddonAssignments] = useState<
    string[] | undefined
  >(undefined);

  const [keyword, setKeyword] = useState('');
  const [tabIndex, setTabIndex] = useState(() => getTabIndex(initialTab));

  const currentWorkspace = useCurrentWorkspace();
  const isOEM = currentWorkspace?.type === WorkspaceType.Oem;

  const { data: assignments, isPending: isAssignmentsLoading } = useCustomQuery(
    {
      query: FIND_ROUTINE_ASSIGNMENTS,
      cacheKey: ['routine', 'assignments', routineId],
      variables: {
        routineId: routineId,
      },
    }
  );

  const { mutateAsync: assignAssetsToRoutine, isPending: isAssetsLoading } =
    useCustomMutation({
      query: ASSIGN_ASSETS_TO_ROUTINE,
    });

  const { mutateAsync: assignAddonsToRoutine, isPending: isAddonsLoading } =
    useCustomMutation({
      query: ASSIGN_ADDONS_TO_ROUTINE,
    });

  useEffect(() => {
    if (assignments && !isAssignmentsLoading) {
      const assets = assignments.routine.findRoutineAssignments.assets;
      const addons = assignments.routine.findRoutineAssignments.addons || [];
      setOriginalAssetAssignments(assets);
      setAssetAssignments(assets);
      setOriginalAddonAssignments(addons);
      setAddonAssignments(addons);
    }
  }, [
    assignments?.routine?.findRoutineAssignments?.assets,
    assignments?.routine?.findRoutineAssignments?.addons,
    isAssignmentsLoading,
  ]);

  useEffect(() => {
    setTabIndex(getTabIndex(initialTab));
  }, [initialTab]);

  const handleAssetsSubmit = async () => {
    const input = {
      assetIds: assetAssignments,
      routineId,
      workspaceId: currentWorkspace.id,
    };

    await assignAssetsToRoutine(
      { input: input },
      {
        onSettled: async () => {
          await queryClient.invalidateQueries({
            queryKey: cacheKeys('routine').all,
          });
        },
      }
    );

    onComplete();
  };

  const handleAddonsSubmit = async () => {
    const input = {
      addonIds: addonAssignments,
      routineId,
      workspaceId: currentWorkspace.id,
    };

    await assignAddonsToRoutine({ input } as any, {
      onSettled: async () => {
        await queryClient.invalidateQueries({
          queryKey: cacheKeys('routine').all,
          exact: false,
        });
        await queryClient.invalidateQueries({
          queryKey: ['addon'],
          exact: false,
        });
      },
    });

    onComplete();
  };

  const isAssetsDirty = () =>
    originalAssetAssignments &&
    assetAssignments &&
    !(
      originalAssetAssignments.length === assetAssignments.length &&
      originalAssetAssignments.every((item) =>
        assetAssignments.includes(item)
      ) &&
      assetAssignments.every((item) => originalAssetAssignments.includes(item))
    );

  const isAddonsDirty = () =>
    originalAddonAssignments &&
    addonAssignments &&
    !(
      originalAddonAssignments.length === addonAssignments.length &&
      originalAddonAssignments.every((item) =>
        addonAssignments.includes(item)
      ) &&
      addonAssignments.every((item) => originalAddonAssignments.includes(item))
    );

  const isDirty = () => (tabIndex === 0 ? isAssetsDirty() : isAddonsDirty());

  const handleSubmit = async () => {
    if (tabIndex === 0) {
      await handleAssetsSubmit();
    } else {
      await handleAddonsSubmit();
    }
  };

  useEffect(() => {
    setKeyword('');
  }, [tabIndex]);

  return (
    <SaveFormTop
      onSubmit={handleSubmit}
      title={title}
      topBar={TopModalBar}
      onCancel={onComplete}
      isDirty={!isAssetsLoading && !isAddonsLoading && !!isDirty()}
    >
      <View style={styles.container}>
        <SearchInput
          value={keyword}
          onChangeText={setKeyword}
          onDebounce={setKeyword}
          debounceLength={3}
          backgroundColor={Colors.white}
          placeholder={t(
            `groups.assignments.${
              tabIndex === 0
                ? 'assetSearchPlaceholder'
                : 'addonsSearchPlaceholder'
            }`
          )}
          height={44}
        />

        <View style={[styles.assignmentContainer]} onLayout={onLayout}>
          <Tabs
            selectedIndex={tabIndex}
            onSelect={(index) => {
              setTabIndex(index);
            }}
          >
            <TabList>
              <Tab>{t('groups.assignments.assets')}</Tab>
              {isOEM && <Tab>{t('product.assignments.addons')}</Tab>}
            </TabList>

            <View style={styles.listContainer}>
              <TabPanel>
                {assetAssignments && currentWorkspace?.id && (
                  <EditRoutineAssetsList
                    workspaceId={currentWorkspace.id}
                    routineId={routineId}
                    maxHeight={listMaxHeight}
                    keyword={keyword}
                    assignments={assetAssignments}
                    updateAssignments={setAssetAssignments}
                  />
                )}
              </TabPanel>
              {isOEM && (
                <TabPanel>
                  {addonAssignments && currentWorkspace?.id && (
                    <RoutineAddonsAssignList
                      workspaceId={currentWorkspace.id}
                      routineId={routineId}
                      maxHeight={listMaxHeight}
                      keyword={keyword}
                      assignments={addonAssignments}
                      updateAssignments={setAddonAssignments}
                    />
                  )}
                </TabPanel>
              )}
            </View>
          </Tabs>
        </View>
      </View>
    </SaveFormTop>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 20,
  },
  assignmentContainer: {
    flex: 1,
    marginTop: 20,
  },
  listContainer: {
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    borderBottomLeftRadius: 12,
    overflow: 'hidden',
  },
  inputStyle: {
    backgroundColor: Colors.white,
    borderColor: Colors.grayDivider,
    borderWidth: 1,
    borderRadius: 8,
  },
});

const ASSIGN_ASSETS_TO_ROUTINE = `
  mutation AssignAssetsToRoutine($input: AssignToRoutineInput!) {
    routine {
      assignToRoutine(input: $input) {
        message
        success
      }
    }
  }
`;

const ASSIGN_ADDONS_TO_ROUTINE = `
  mutation AssignAddonsToRoutine($input: AssignToRoutineInput!) {
    routine {
      assignToRoutine(input: $input) {
        message
        success
      }
    }
  }
`;
